import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Settings as SettingsIcon, LogOut, X } from 'lucide-react';
import { useData } from './hooks/useData';
import { pushWidgetSnapshot } from './lib/widgetBridge';
import { useTheme } from './lib/theme';
import { listenForDeepLinks } from './lib/deepLinks';
import { drainWidgetActions, applyWidgetActions } from './lib/widgetActions';
import { refreshMyLabels } from './lib/myLabels';
import { stampLocale } from './lib/notificationPrefs';
import { subscribeToChanges } from './lib/realtimeSync';
import { mirrorSessionToNative, adoptNativeSession } from './lib/nativeSession';
import { initNativePush } from './lib/pushNotifications';
import { useAuth } from './lib/AuthContext';
import { useTranslation } from './lib/i18n';
import Auth from './components/Auth';
import NameOnboarding from './components/NameOnboarding';
import { loadProfile } from './lib/profile';
import { expandEvents } from './utils/recurrence';
import { format } from 'date-fns';
import Dashboard from './components/Dashboard';
import Budget from './components/Budget';
import Savings from './components/Savings';
import Fitness from './components/Fitness';
import Calendar from './components/Calendar';
import BirthdayCalendar from './components/BirthdayCalendar';
import ShoppingList from './components/ShoppingList';
import FuelLog from './components/FuelLog';
import Tutoring from './components/Tutoring';
import Projects from './components/Projects';
import Settings from './components/Settings';
import Todo from './components/Todo';
import Reminders from './components/Reminders';
import Diary from './components/Diary';
import HabitTracker from './components/HabitTracker';
import TimeTracker from './components/TimeTracker';
import InstallHint from './components/InstallHint';
import BottomNav from './components/BottomNav';
import { FEATURE_GROUPS, getGroupById } from './featureRegistry';
import { Users as UsersIcon } from 'lucide-react';
import { joinSharedCalendarByToken } from './lib/sharedCalendars';
import { joinSharedShoppingListByToken } from './lib/sharedShoppingLists';
import { joinFuelLogByToken } from './lib/fuelLogs';
import { useToast } from './lib/Toast';


function App() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  // Default: all folders collapsed. User-opened folders stay open until closed.
  const [expandedMenu, setExpandedMenu] = useState({});
  // Mobile drawer state. On lg+ the sidebar is permanent and this is ignored.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // When the user picks a page on mobile, close the drawer so it doesn't
  // stay sitting over the content.
  // Bumped on every calendar tab press (including re-pressing the one you
  // are already on) so the Calendar jumps back to the current month —
  // otherwise a month you swiped to weeks ago is still showing.
  const [calendarHomeSignal, setCalendarHomeSignal] = useState(0);
  const goToTab = (id) => {
    if (id === 'calendar' || (typeof id === 'string' && id.startsWith('shared:'))) {
      setCalendarHomeSignal((n) => n + 1);
    }
    setActiveTab(id);
    setSidebarOpen(false);
  };
  const { data, isLoaded, updateData, exportData, importData, importFromLocalStorage, reload, silentReload, syncError } = useData();
  const toast = useToast();

  // Keep the iOS home-screen widget in sync (no-op on the web). The theme is
  // in the deps too: the widget paints itself light or dark from the app's
  // setting, so flipping dark mode has to push a fresh snapshot even when no
  // data changed.
  const { resolvedMode, color: themeColor } = useTheme();
  useEffect(() => {
    if (isLoaded) pushWidgetSnapshot(data);
  }, [data, isLoaded, resolvedMode, themeColor]);

  // Widget taps: open the day, the month, or a new event in the right
  // calendar. `nonce` makes repeated taps on the same day re-trigger.
  const [calendarIntent, setCalendarIntent] = useState(null);
  // The listener is installed once, so it reads `data` through a ref rather
  // than capturing the first render's copy.
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    const stop = listenForDeepLinks((link) => {
      // The domain widgets (To-dos, Timer, …) just open their page.
      if (link.action === 'open') {
        setActiveTab(link.tab);
        setSidebarOpen(false);
        return;
      }
      // A widget can outlive the calendar it was configured for — the user may
      // have left the shared calendar or hidden it since. Fall back to the
      // personal calendar rather than opening a tab that renders nothing.
      const exists =
        link.calendarId === 'personal' ||
        (dataRef.current?.calendar?.sharedCalendars || []).some(
          (c) => c.id === link.calendarId
        );
      const target = exists ? link : { ...link, tab: 'calendar', calendarId: 'personal' };
      setActiveTab(target.tab);
      setSidebarOpen(false);
      setCalendarIntent({ ...target, nonce: Date.now() });
    });
    return stop;
  }, []);
  // The lock-screen widget breaks same-time ties by "my label" in each shared
  // calendar. Members come from an RPC, not from `data`, so fetch them once
  // per set of calendars and re-push the snapshot if anything changed.
  const sharedIds = (data?.calendar?.sharedCalendars || []).map((c) => c.id).join(',');
  useEffect(() => {
    if (!isLoaded || !user) return undefined;
    let cancelled = false;
    refreshMyLabels(dataRef.current?.calendar?.sharedCalendars, user.id).then((changed) => {
      if (!cancelled && changed) pushWidgetSnapshot(dataRef.current);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, user, sharedIds]);

  // The server-side reminders/digest need to know the language to word the
  // message in and the zone the personal digest time is meant in. Keep both
  // stamped on the notification prefs (only writes when something changed).
  const { lang: uiLang } = useTranslation();
  useEffect(() => {
    if (!isLoaded || !user) return;
    const settings = dataRef.current?.settings || {};
    const stamped = stampLocale(settings.notificationPrefs, uiLang);
    if (stamped) updateData({ settings: { ...settings, notificationPrefs: stamped } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, user, uiLang]);

  // Taps inside the home-screen widgets (tick off a to-do, stop the timer)
  // queue outcomes the widget can't apply itself. Drain and apply them as
  // soon as data is loaded and every time the app returns to the foreground,
  // so the change reaches the server through the normal path.
  useEffect(() => {
    if (!isLoaded) return undefined;
    let cancelled = false;
    const drain = async () => {
      // The widget may have refreshed the session meanwhile; take the newer
      // token pair before anything else talks to the server.
      await adoptNativeSession();
      const actions = await drainWidgetActions();
      if (cancelled || actions.length === 0) return;
      const updates = applyWidgetActions(dataRef.current, actions);
      if (updates) updateData(updates);
    };
    drain();
    const onVisible = () => {
      if (document.visibilityState === 'visible') drain();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoaded, updateData]);

  // Surface sync errors as a transient toast instead of a persistent footer
  // banner. We track the last shown message so the same error doesn't
  // re-toast on every render.
  const lastSyncErrorRef = useRef(null);
  useEffect(() => {
    if (syncError && syncError !== lastSyncErrorRef.current) {
      lastSyncErrorRef.current = syncError;
      toast.error(t('app.syncError', { message: syncError }));
    } else if (!syncError) {
      lastSyncErrorRef.current = null;
    }
  }, [syncError, toast, t]);
  const inviteHandledRef = useRef(false);
  const autoRecordRunRef = useRef(false);
  const initialTabSetRef = useRef(false);

  // Profile name gate: forces new users to choose a display name on first sign-in.
  // null = not yet checked, true = name is set, false = needs onboarding.
  const [hasName, setHasName] = useState(null);
  useEffect(() => {
    if (!user) {
      setHasName(null);
      return;
    }
    let cancelled = false;
    loadProfile()
      .then((p) => {
        if (cancelled) return;
        setHasName(!!(p && p.displayName && p.displayName.trim()));
      })
      .catch(() => {
        if (!cancelled) setHasName(true); // fail-open: don't block on transient error
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // On initial load, jump to the first visible item in the user's sorted menu.
  // Mirrors the sidebar visibility rules so we never land on a hidden tab.
  useEffect(() => {
    if (!isLoaded || initialTabSetRef.current) return;
    const settings = data.settings || {};
    const enabled = settings.enabledFeatures || {};
    const showPersonalCalendar = settings.showPersonalCalendar !== false;
    const sharedCalendars = data.calendar?.sharedCalendars || [];
    const order =
      settings.groupOrder && settings.groupOrder.length > 0
        ? settings.groupOrder
        : FEATURE_GROUPS.map((g) => g.id);

    for (const gid of order) {
      const group = getGroupById(gid);
      if (!group) continue;

      // Same filter the sidebar applies: enabled items, with the personal
      // calendar suppressed when the toggle is off, and any visible shared
      // calendars injected after the personal calendar slot.
      let visibleIds = (group.items || [])
        .filter((it) => enabled[it.id] !== false)
        .map((it) => it.id);

      if (group.id === 'planning') {
        if (!showPersonalCalendar) {
          visibleIds = visibleIds.filter((id) => id !== 'calendar');
        }
        const sharedIds = sharedCalendars
          .filter((sc) => enabled[`shared:${sc.id}`] !== false)
          .map((sc) => `shared:${sc.id}`);
        const calIdx = visibleIds.indexOf('calendar');
        if (calIdx >= 0) {
          visibleIds = [
            ...visibleIds.slice(0, calIdx + 1),
            ...sharedIds,
            ...visibleIds.slice(calIdx + 1)
          ];
        } else {
          visibleIds = [...sharedIds, ...visibleIds];
        }
      }

      if (visibleIds.length > 0) {
        setActiveTab(visibleIds[0]);
        initialTabSetRef.current = true;
        return;
      }
    }
    initialTabSetRef.current = true;
  }, [isLoaded, data]);

  // Real-time-ish refresh: poll every 5 s while the tab is visible so other
  // users' changes (shared calendars, labels, events) appear automatically.
  // `silentReload` is a no-op while writes are in flight so this won't
  // interfere with optimistic local edits.
  useEffect(() => {
    if (!isLoaded || !user) return;
    // Live: Supabase Realtime pushes row changes; each burst becomes one
    // silent reload. Polling remains as the safety net — every 60 s while
    // the channel is subscribed, every 5 s when it is not (so a broken
    // WebSocket degrades to the old behaviour, never to a stale app).
    const live = subscribeToChanges(user.id, () => {
      if (document.visibilityState === 'visible') silentReload();
    });
    const INTERVAL = 5000;
    const LIVE_INTERVAL = 60000;
    let lastReload = Date.now();
    let timer = null;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (live.isLive() && Date.now() - lastReload < LIVE_INTERVAL) return;
      lastReload = Date.now();
      silentReload();
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, INTERVAL);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        silentReload(); // catch up immediately when tab regains focus
        start();
      } else {
        stop();
      }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      live.stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isLoaded, user, silentReload]);

  // The widgets keep a copy of the session (shared Keychain) so they can
  // write to Supabase directly; mirror ours to them on every change.
  useEffect(() => mirrorSessionToNative(), []);
  // Native push: refresh a previously registered device token and wire taps.
  useEffect(() => {
    if (user) initNativePush();
  }, [user]);

  // Handle ?joinCalendar=<token> invite links on first load.
  useEffect(() => {
    if (!user || inviteHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('joinCalendar');
    if (!token) return;
    inviteHandledRef.current = true;

    (async () => {
      try {
        const cid = await joinSharedCalendarByToken(token);
        const refreshed = await reload();
        // Clean URL so a refresh doesn't rejoin in a loop
        params.delete('joinCalendar');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}`
        );
        if (cid) {
          setActiveTab(`shared:${cid}`);
          setExpandedMenu((p) => ({ ...p, planning: true }));
        }
        // Find the calendar name for a friendly toast.
        const cal = (refreshed?.calendar?.sharedCalendars || []).find(
          (c) => c.id === cid
        );
        toast.success(
          cal?.name
            ? `Kalender „${cal.name}“ beigetreten`
            : 'Kalender beigetreten'
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to join shared calendar', err);
        toast.error(t('sharedCalendar.joinFailed', { message: err.message || '' }));
      }
    })();
  }, [user, reload, t, toast]);

  // Handle ?joinFuelLog=<token> invite links on first load.
  const inviteFuelHandledRef = useRef(false);
  useEffect(() => {
    if (!user || inviteFuelHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('joinFuelLog');
    if (!token) return;
    inviteFuelHandledRef.current = true;

    (async () => {
      try {
        await joinFuelLogByToken(token);
        params.delete('joinFuelLog');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}`
        );
        setActiveTab('fuel');
        toast.success('Tank-Liste beigetreten');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to join fuel log', err);
        toast.error(
          'Konnte der Tank-Liste nicht beitreten: ' + (err.message || '')
        );
      }
    })();
  }, [user, toast]);

  // Handle ?joinShoppingList=<token> invite links on first load.
  const inviteShoppingHandledRef = useRef(false);
  useEffect(() => {
    if (!user || inviteShoppingHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('joinShoppingList');
    if (!token) return;
    inviteShoppingHandledRef.current = true;

    (async () => {
      try {
        await joinSharedShoppingListByToken(token);
        await reload();
        params.delete('joinShoppingList');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}`
        );
        setActiveTab('shopping');
        toast.success('Einkaufsliste beigetreten');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to join shared shopping list', err);
        toast.error(
          'Konnte der geteilten Einkaufsliste nicht beitreten: ' +
            (err.message || '')
        );
      }
    })();
  }, [user, reload, toast]);

  // Auto-record sport workouts from past occurrences of sport-linked recurring events.
  // Also removes orphaned auto-created workouts whose calendar occurrence was deleted.
  useEffect(() => {
    if (!isLoaded || autoRecordRunRef.current) return;
    autoRecordRunRef.current = true;

    const events = data.calendar?.events || [];
    const sportEvents = events.filter((ev) => ev.linkedSportName);

    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const todayStr = format(today, 'yyyy-MM-dd');

    // Compute duration in minutes from startTime/endTime
    const computeDuration = (start, end) => {
      if (!start || !end) return '';
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      let mins = eh * 60 + em - (sh * 60 + sm);
      if (mins < 0) mins += 24 * 60;
      return mins > 0 ? String(mins) : '';
    };

    // Build expected (eventId, date) pairs
    const expectedKeys = new Set();
    const eventById = {};
    for (const ev of sportEvents) {
      eventById[ev.id] = ev;
      const expanded = expandEvents([ev], new Date(ev.date), today);
      for (const occ of expanded) {
        if (occ.date > todayStr) continue;
        expectedKeys.add(`${ev.id}::${occ.date}`);
      }
    }

    const workoutsBySport = { ...(data.fitness?.workoutsBySport || {}) };
    let changed = false;

    // 1) Add missing auto entries
    for (const ev of sportEvents) {
      const sportName = ev.linkedSportName;
      const existing = workoutsBySport[sportName] || [];
      const existingIds = new Set(existing.map((w) => w.id));
      const expanded = expandEvents([ev], new Date(ev.date), today);
      for (const occ of expanded) {
        if (occ.date > todayStr) continue;
        const autoId = `auto-${ev.id}-${occ.date}`;
        if (existingIds.has(autoId)) continue;
        const workout = {
          id: autoId,
          date: occ.date,
          startTime: ev.startTime || '',
          duration: computeDuration(ev.startTime, ev.endTime),
          notes: ev.notes || '',
          sourceEventId: ev.id,
          sourceOccurrenceDate: occ.date,
          autoCreated: true
        };
        workoutsBySport[sportName] = [...(workoutsBySport[sportName] || []), workout];
        changed = true;
      }
    }

    // 2) Remove orphaned auto entries whose source occurrence no longer exists,
    //    AND remove auto entries with empty duration (legacy entries before duration was auto-filled).
    for (const sportName of Object.keys(workoutsBySport)) {
      const list = workoutsBySport[sportName] || [];
      const filtered = list.filter((w) => {
        if (!w.autoCreated || !w.sourceEventId) return true; // manual entry, keep
        if (!w.duration || String(w.duration).trim() === '') return false; // empty-duration auto: drop
        const key = `${w.sourceEventId}::${w.sourceOccurrenceDate}`;
        return expectedKeys.has(key);
      });
      if (filtered.length !== list.length) {
        workoutsBySport[sportName] = filtered;
        changed = true;
      }
    }

    // 3) Deduplicate workouts by date per sport. Prefer the one with the most data:
    //    has duration > has notes > autoCreated (linked) > first encountered.
    const score = (w) => {
      let s = 0;
      if (w.duration && String(w.duration).trim() !== '') s += 4;
      if (w.notes && String(w.notes).trim() !== '') s += 2;
      if (w.autoCreated) s += 1;
      return s;
    };
    for (const sportName of Object.keys(workoutsBySport)) {
      const list = workoutsBySport[sportName] || [];
      const byDate = new Map();
      for (const w of list) {
        const existing = byDate.get(w.date);
        if (!existing || score(w) > score(existing)) {
          byDate.set(w.date, w);
        }
      }
      const deduped = Array.from(byDate.values());
      if (deduped.length !== list.length) {
        workoutsBySport[sportName] = deduped;
        changed = true;
      }
    }

    if (changed) {
      updateData({
        fitness: {
          ...data.fitness,
          workoutsBySport
        }
      });
    }
  }, [isLoaded, data, updateData]);

  const toggleMenu = (menuId) => {
    setExpandedMenu(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg font-medium text-gray-600">{t('app.loading')}</div>
      </div>
    );
  }

  if (!user) {
    return <Auth />;
  }

  if (hasName === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg font-medium text-gray-600">{t('app.loading')}</div>
      </div>
    );
  }

  if (hasName === false) {
    return <NameOnboarding onDone={() => setHasName(true)} />;
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg font-medium text-gray-600">{t('app.loading')}</div>
      </div>
    );
  }

  const renderContent = () => {
    if (typeof activeTab === 'string' && activeTab.startsWith('shared:')) {
      const id = activeTab.slice('shared:'.length);
      return (
        <Calendar
          data={data}
          updateData={updateData}
          reload={reload}
          sharedCalendarId={id}
          homeSignal={calendarHomeSignal}
          intent={calendarIntent && calendarIntent.tab === activeTab ? calendarIntent : null}
        />
      );
    }
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard data={data} updateData={updateData} />;
      case 'budget':
        return <Budget data={data} updateData={updateData} />;
      case 'savings':
        return <Savings data={data} updateData={updateData} />;
      case 'fitness':
        return <Fitness data={data} updateData={updateData} viewMode="overview" />;
      case 'sports':
        return <Fitness data={data} updateData={updateData} viewMode="sports" />;
      case 'calendar':
        return (
          <Calendar
            data={data}
            updateData={updateData}
            reload={reload}
            homeSignal={calendarHomeSignal}
            intent={calendarIntent && calendarIntent.tab === 'calendar' ? calendarIntent : null}
          />
        );
      case 'birthdays':
        return <BirthdayCalendar data={data} updateData={updateData} />;
      case 'todo':
        return <Todo data={data} updateData={updateData} />;
      case 'reminders':
        return <Reminders data={data} updateData={updateData} />;
      case 'diary':
        return <Diary data={data} updateData={updateData} />;
      case 'habits':
        return <HabitTracker data={data} updateData={updateData} />;
      case 'timetracker':
        return <TimeTracker data={data} updateData={updateData} />;
      case 'shopping':
        return <ShoppingList data={data} updateData={updateData} />;
      case 'fuel':
        return <FuelLog />;
      case 'tutoring':
        return <Tutoring />;
      case 'projects':
        return <Projects />;
      case 'settings':
        return (
          <Settings
            data={data}
            updateData={updateData}
            importFromLocalStorage={importFromLocalStorage}
            exportData={exportData}
            importData={importData}
            onSignOut={signOut}
          />
        );
      default:
        return <Dashboard data={data} updateData={updateData} />;
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Mobile overlay: dims the background when the drawer is open. Tap to
          close. Hidden on lg+ where the sidebar is permanent. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar. On lg+ it's a permanent sticky column; below lg it becomes
          a slide-in drawer that sits over the content (not full-screen, so
          some of the main content stays visible under the overlay). */}
      <aside
        className={`w-64 bg-gray-900 text-white flex flex-col h-screen fixed lg:sticky inset-y-0 left-0 z-40 transform transition-transform duration-200 lg:translate-x-0 flex-shrink-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div
          className="p-6 border-b border-gray-800 flex items-center justify-between"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
        >
          <div>
            <h1 className="text-xl font-bold">{t('app.title')}</h1>
            <p className="text-sm text-gray-400 mt-1">{t('app.tagline')}</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-800 lg:hidden"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto no-scrollbar">
          {(() => {
            const settings = data.settings || {};
            const enabled = settings.enabledFeatures || {};
            const order = settings.groupOrder && settings.groupOrder.length > 0
              ? settings.groupOrder
              : FEATURE_GROUPS.map((g) => g.id);

            const orderedGroups = order
              .map((id) => getGroupById(id))
              .filter(Boolean);

            const sharedCalendars = data.calendar?.sharedCalendars || [];
            const showPersonalCalendar = settings.showPersonalCalendar !== false;

            return orderedGroups.map((group) => {
              let visibleItems = group.items.filter(
                (it) => enabled[it.id] !== false
              );
              // Inject shared calendars + personal-calendar toggle into 'planning'
              if (group.id === 'planning') {
                visibleItems = visibleItems.filter(
                  (it) => it.id !== 'calendar' || showPersonalCalendar
                );
                const sharedItems = sharedCalendars
                  .filter((sc) => enabled[`shared:${sc.id}`] !== false)
                  .map((sc) => ({
                    id: `shared:${sc.id}`,
                    label: sc.name,
                    rawLabel: true,
                    icon: UsersIcon
                  }));
                // Place shared calendars right after the personal calendar entry.
                const calIdx = visibleItems.findIndex((it) => it.id === 'calendar');
                if (calIdx >= 0) {
                  visibleItems = [
                    ...visibleItems.slice(0, calIdx + 1),
                    ...sharedItems,
                    ...visibleItems.slice(calIdx + 1)
                  ];
                } else {
                  visibleItems = [...sharedItems, ...visibleItems];
                }
              }
              if (visibleItems.length === 0) return null;

              const Icon = group.icon;
              const hasSubmenu = visibleItems.length > 1;
              const isExpanded = expandedMenu[group.id];
              const groupActive = visibleItems.some((it) => it.id === activeTab);

              const primaryItem = visibleItems[0];

              return (
                <div key={group.id}>
                  <button
                    onClick={() => {
                      if (hasSubmenu) {
                        // Folder behaviour: just fold open/closed. Don't
                        // navigate and don't close the mobile drawer — the
                        // user is still browsing the menu and the page in
                        // the background should stay on the previous tab.
                        toggleMenu(group.id);
                      } else {
                        goToTab(primaryItem.id);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      groupActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="flex-1 text-left">{t(group.label)}</span>
                    {hasSubmenu && (
                      <span className="text-gray-400">
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                    )}
                  </button>
                  {hasSubmenu && isExpanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {visibleItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <button
                            key={subItem.id}
                            onClick={() => goToTab(subItem.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              activeTab === subItem.id
                                ? 'bg-primary-700 text-white'
                                : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                            }`}
                          >
                            {SubIcon && <SubIcon size={16} />}
                            <span className="truncate">{subItem.rawLabel ? subItem.label : t(subItem.label)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </nav>

        <div
          className="p-4 border-t border-gray-800 space-y-2"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          <button
            onClick={() => goToTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'settings'
                ? 'bg-primary-600 text-white'
                : 'text-gray-300 hover:bg-gray-800'
            }`}
          >
            <SettingsIcon size={18} />
            <span>{t('app.settings')}</span>
          </button>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
            title={user?.email || ''}
          >
            <LogOut size={18} />
            <span className="truncate">{t('app.signOut')}</span>
          </button>
          {/* Sync errors are surfaced as a toast instead of a persistent
              banner here — see useEffect on syncError above. */}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto overscroll-none min-w-0">
        {/* The status-bar / Dynamic Island inset is applied once, on #root
            (see index.css) — adding it here as well stacked up to a huge
            empty strip on iPhone. Only the bottom inset is local, to clear
            the fixed mobile tab bar (~58px + home indicator). */}
        <div
          className="p-4 sm:p-6 lg:p-8"
          style={{
            // Start below the status bar; scrolled content passes beneath
            // the blurred strip rendered at the end of this component.
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)'
          }}
        >
          {renderContent()}
        </div>
      </main>
      {/* iOS Safari first-time visitors get a one-shot "Add to Home Screen"
          hint here. Self-hides on Android, in standalone PWA, or once
          dismissed. */}
      {/* Status-bar strip: content scrolls underneath it, blurred, so the
          clock and battery stay readable — the standard iOS treatment.
          Zero height in a browser tab, where the inset is 0. */}
      <div
        className="status-scrim fixed top-0 left-0 right-0 pointer-events-none"
        style={{ zIndex: 45 }}
        aria-hidden="true"
      />
      {/* No onSignOut: signing out lives in Settings ▸ account, not in the
          Mehr sheet. The desktop sidebar above keeps its own. */}
      <BottomNav data={data} activeTab={activeTab} onSelect={goToTab} />
      <InstallHint />
    </div>
  );
}

export default App;
